import {
  buildFieldErrors,
  buildGlobalErrors,
  type UserError,
} from '@/lib/graphql/errors';
import { useMutation } from '@/lib/graphql/hooks';
import { graphql } from '@/lib/graphql/schema';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const REQUEST_EMAIL_CHANGE_MUTATION = graphql(`
  mutation requestEmailChange($input: RequestEmailChangeInput!) {
    requestEmailChange(input: $input) {
      success
      pendingEmail
      userErrors {
        message
        code
        path
      }
    }
  }
`);

export const useEmailChangeForm = (currentEmail: string | null | undefined) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [requestedEmail, setRequestedEmail] = useState<string | null>(null);
  const [requestEmailChange, { data, loading, error, reset }] = useMutation(
    REQUEST_EMAIL_CHANGE_MUTATION
  );

  const normalizedCurrentEmail = (currentEmail ?? '').trim().toLowerCase();
  const normalizedEmail = email.trim().toLowerCase();
  const hasChange =
    normalizedEmail !== '' && normalizedEmail !== normalizedCurrentEmail;
  const isBrowserValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    normalizedEmail
  );
  const canSubmit =
    hasChange && isBrowserValidEmail && currentPassword !== '' && !loading;

  const resetMutationResult = () => {
    setRequestedEmail(null);

    if (!loading && (data || error)) {
      reset();
    }
  };

  const handleEmailChange = (value: string) => {
    resetMutationResult();
    setEmail(value);
  };

  const handleCurrentPasswordChange = (value: string) => {
    resetMutationResult();
    setCurrentPassword(value);
  };

  const mutationErrors = data?.requestEmailChange?.userErrors ?? [];
  const fieldErrors = buildFieldErrors(mutationErrors);
  const globalErrors = [
    ...buildGlobalErrors(mutationErrors, ['email', 'currentPassword']),
    ...(error
      ? ([
          {
            code: 'NETWORK_ERROR',
            message: t(
              'メールアドレス変更の申請に失敗しました。時間をおいて再度お試しください。'
            ),
          },
        ] satisfies UserError[])
      : []),
  ];

  const onSubmit = async () => {
    if (!canSubmit) return;

    try {
      const result = await requestEmailChange({
        variables: {
          input: { email: normalizedEmail, currentPassword },
        },
      });
      const response = result.data?.requestEmailChange;
      if (response?.success) {
        setRequestedEmail(response.pendingEmail ?? normalizedEmail);
        setEmail('');
        setCurrentPassword('');
      }
    } catch {
      // Apollo exposes the mutation error through the result object.
    }
  };

  return {
    email,
    currentPassword,
    requestedEmail,
    fieldErrors,
    globalErrors,
    canSubmit,
    isSaving: loading,
    onSubmit,
    setEmail: handleEmailChange,
    setCurrentPassword: handleCurrentPasswordChange,
  };
};
